import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { UserService, LicenseType, RankType } from '../../services/user.service';

@Component({
  selector: 'app-create-user-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './create-user-modal.component.html',
  styleUrl: './create-user-modal.component.scss'
})
export class CreateUserModalComponent implements OnInit, OnDestroy {
  createUserForm!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isSubmitting = false;
  licenses: LicenseType[] = [];
  ranks: RankType[] = [];
  isLoadingData = false;
  
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private userService: UserService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadSelectData();
    this.setupAddressWatchers();
    this.initializeDynamicArrays();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.createUserForm = this.fb.group({
      // Personal Information
      firstName: ['', [Validators.minLength(2)]],
      lastName: ['', [Validators.minLength(2)]],
      email: ['', [Validators.email]],
      cid: ['', [Validators.pattern(/^\d{13}$/)]],
      phoneNumber: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      gender: [''],
      dateOfBirth: [''],
      
      // Registered Address Information
      registeredHouseNumber: [''],
      registeredProjectName: [''],
      registeredSoi: [''],
      registeredRoad: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: ['', Validators.pattern(/^\d{5}$/)],
      
      // Current Address Information
      currentHouseNumber: [''],
      currentProjectName: [''],
      currentSoi: [''],
      currentRoad: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: ['', Validators.pattern(/^\d{5}$/)],
      
      // Address Options
      sameAsRegistered: [false],
      
      // Professional Information
      organization: [''],
      licenseType: [''],
      rankLevel: [''],
      
      // VAT Member Information
      vatMemberType: [''],
      vatMemberExpired: [''],
      vatMemberId: [''],
      
      // Valuer Rank Information
      valuerRank: [''],
      valuerRankExpired: [''],
      
      // VAT Information
      vatId: [''],
      vatExpired: [''],
      
      // Valuer Information
      valuerId: [''],
      valuerExpired: [''],
      
      // Valuation Experience (Dynamic Array)
      valuationExperience: this.fb.array([]),
      
      // Education Background (Dynamic Array)
      educationBackground: this.fb.array([]),
      
      // Professional Training (Dynamic Array)
      professionalTraining: this.fb.array([]),
      
      // Other Training (Dynamic Array)
      otherTraining: this.fb.array([]),
      
      // Initial Status
      isActive: [true]
    });
  }

  /**
   * Initialize dynamic arrays with default rows
   */
  private initializeDynamicArrays(): void {
    // Add one default row for valuation experience
    this.addValuationExperience();
    
    // Add one default row for education background
    this.addEducationBackground();
    
    // Add one default row for professional training
    this.addProfessionalTraining();
    
    // Add one default row for other training
    this.addOtherTraining();
  }

  /**
   * Setup watchers for registered address fields to auto-copy when checkbox is checked
   */
  private setupAddressWatchers(): void {
    const registeredFields = [
      'registeredHouseNumber',
      'registeredProjectName',
      'registeredSoi',
      'registeredRoad',
      'registeredSubdistrict',
      'registeredDistrict',
      'registeredProvince',
      'registeredPostalCode'
    ];

    registeredFields.forEach(fieldName => {
      const control = this.createUserForm.get(fieldName);
      if (control) {
        const subscription = control.valueChanges.subscribe(() => {
          if (this.createUserForm.get('sameAsRegistered')?.value) {
            this.copyRegisteredToCurrent();
          }
        });
        this.subscription.add(subscription);
      }
    });
  }

  /**
   * Copy registered address data to current address fields
   */
  private copyRegisteredToCurrent(): void {
    const registeredData = {
      currentHouseNumber: this.createUserForm.get('registeredHouseNumber')?.value || '',
      currentProjectName: this.createUserForm.get('registeredProjectName')?.value || '',
      currentSoi: this.createUserForm.get('registeredSoi')?.value || '',
      currentRoad: this.createUserForm.get('registeredRoad')?.value || '',
      currentSubdistrict: this.createUserForm.get('registeredSubdistrict')?.value || '',
      currentDistrict: this.createUserForm.get('registeredDistrict')?.value || '',
      currentProvince: this.createUserForm.get('registeredProvince')?.value || '',
      currentPostalCode: this.createUserForm.get('registeredPostalCode')?.value || ''
    };
    this.createUserForm.patchValue(registeredData);
  }

  /**
   * Load select box data (licenses and ranks)
   */
  private loadSelectData(): void {
    this.isLoadingData = true;

    const licensesSub = this.userService.getAllLicenses().subscribe({
      next: (licenses) => {
        this.licenses = licenses;
      },
      error: (error) => {
        console.error('Error loading licenses:', error);
      }
    });

    const ranksSub = this.userService.getAllRanks().subscribe({
      next: (ranks) => {
        this.ranks = ranks;
        this.isLoadingData = false;
      },
      error: (error) => {
        console.error('Error loading ranks:', error);
        this.isLoadingData = false;
      }
    });

    this.subscription.add(licensesSub);
    this.subscription.add(ranksSub);
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      // Validate current step before proceeding
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // Only allow going to previous steps or current step
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    // Allow all steps to pass validation for easier testing
    // Only validate format/pattern errors, not required fields
    return true;
  }

  /**
   * Validate specific fields
   */
  private validateFields(fieldNames: string[]): boolean {
    let isValid = true;
    
    fieldNames.forEach(fieldName => {
      const control = this.createUserForm.get(fieldName);
      if (control) {
        control.markAsTouched();
        if (control.invalid) {
          isValid = false;
        }
      }
    });
    
    return isValid;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.createUserForm.get(fieldName);
    if (!control) return false;
    
    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }
    
    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.createUserForm.get(fieldName);
    if (!control || !control.errors) return '';
    
    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (control.errors['minlength']) return `ต้องมีอย่างน้อย ${control.errors['minlength'].requiredLength} ตัวอักษร`;
    if (control.errors['pattern']) {
      switch (fieldName) {
        case 'cid': return 'เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก';
        case 'phoneNumber': return 'หมายเลขโทรศัพท์ต้องเป็นตัวเลข 10 หลัก';
        case 'registeredPostalCode':
        case 'currentPostalCode': return 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก';
        default: return 'รูปแบบข้อมูลไม่ถูกต้อง';
      }
    }
    
    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Format CID input (auto-format with dashes)
   */
  onCidInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 13) {
      value = value.substring(0, 13);
    }
    this.createUserForm.patchValue({ cid: value });
  }

  /**
   * Format phone number input
   */
  onPhoneInput(event: any): void {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    this.createUserForm.patchValue({ phoneNumber: value });
  }

  /**
   * Handle same as registered address checkbox
   */
  onSameAsRegisteredChange(event: any): void {
    const isChecked = event.target.checked;
    this.createUserForm.patchValue({ sameAsRegistered: isChecked });
    
    if (isChecked) {
      // Copy registered address to current address
      this.copyRegisteredToCurrent();
      
      // Disable current address fields
      this.toggleCurrentAddressFields(false);
    } else {
      // Enable current address fields
      this.toggleCurrentAddressFields(true);
    }
  }

  /**
   * Toggle current address fields enabled/disabled state
   */
  private toggleCurrentAddressFields(enabled: boolean): void {
    const currentAddressFields = [
      'currentHouseNumber',
      'currentProjectName',
      'currentSoi',
      'currentRoad',
      'currentSubdistrict', 
      'currentDistrict',
      'currentProvince',
      'currentPostalCode'
    ];

    currentAddressFields.forEach(fieldName => {
      const control = this.createUserForm.get(fieldName);
      if (control) {
        if (enabled) {
          control.enable();
        } else {
          control.disable();
        }
      }
    });
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    if (this.createUserForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      
      const formData = this.createUserForm.value;
      console.log('Creating new user with data:', formData);
      
      // TODO: Replace with actual API call
      setTimeout(() => {
        console.log('User created successfully!');
        this.isSubmitting = false;
        this.activeModal.close({
          success: true,
          userData: formData
        });
      }, 2000);
      
      // When API is ready, use this:
      /*
      const createSub = this.userService.createUser(formData).subscribe({
        next: (response) => {
          console.log('User created successfully:', response);
          this.isSubmitting = false;
          this.activeModal.close({
            success: true,
            userData: response
          });
        },
        error: (error) => {
          console.error('Error creating user:', error);
          this.isSubmitting = false;
          // Handle error (show error message)
        }
      });
      
      this.subscription.add(createSub);
      */
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.createUserForm.controls).forEach(key => {
        this.createUserForm.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลส่วนตัว';
      case 2: return 'ข้อมูลการทำงาน';
      case 3: return 'ตรวจสอบและยืนยัน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-person-circle';
      case 2: return 'bi-briefcase';
      case 3: return 'bi-check-circle';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Get license name by type
   */
  getLicenseName(licenseType: string): string {
    if (!licenseType || !this.licenses) return '';
    const license = this.licenses.find(l => l.licenseType === licenseType);
    return license ? license.name : licenseType;
  }

  /**
   * Get rank name by level
   */
  getRankName(rankLevel: string): string {
    if (!rankLevel || !this.ranks) return '';
    const levelNumber = parseInt(rankLevel, 10);
    const rank = this.ranks.find(r => r.level === levelNumber);
    return rank ? rank.name : rankLevel;
  }

  /**
   * Format date of birth for display
   */
  formatDateOfBirth(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Get valuation experience form array
   */
  get valuationExperienceArray(): FormArray {
    return this.createUserForm.get('valuationExperience') as FormArray;
  }

  /**
   * Get education background form array
   */
  get educationBackgroundArray(): FormArray {
    return this.createUserForm.get('educationBackground') as FormArray;
  }

  /**
   * Add new valuation experience row
   */
  addValuationExperience(): void {
    const experienceGroup = this.fb.group({
      organization: [''],
      position: [''],
      years: ['']
    });
    this.valuationExperienceArray.push(experienceGroup);
  }

  /**
   * Remove valuation experience row
   */
  removeValuationExperience(index: number): void {
    if (this.valuationExperienceArray.length > 0) {
      this.valuationExperienceArray.removeAt(index);
    }
  }

  /**
   * Add new education background row
   */
  addEducationBackground(): void {
    const educationGroup = this.fb.group({
      institution: [''],
      degree: [''],
      yearComplete: ['']
    });
    this.educationBackgroundArray.push(educationGroup);
  }

  /**
   * Remove education background row
   */
  removeEducationBackground(index: number): void {
    if (this.educationBackgroundArray.length > 0) {
      this.educationBackgroundArray.removeAt(index);
    }
  }

  /**
   * Get full address string for display
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const addressParts: string[] = [];
    
    const houseNumber = this.createUserForm.get(`${prefix}HouseNumber`)?.value;
    const projectName = this.createUserForm.get(`${prefix}ProjectName`)?.value;
    const soi = this.createUserForm.get(`${prefix}Soi`)?.value;
    const road = this.createUserForm.get(`${prefix}Road`)?.value;
    const subdistrict = this.createUserForm.get(`${prefix}Subdistrict`)?.value;
    const district = this.createUserForm.get(`${prefix}District`)?.value;
    const province = this.createUserForm.get(`${prefix}Province`)?.value;
    const postalCode = this.createUserForm.get(`${prefix}PostalCode`)?.value;
    
    if (houseNumber) addressParts.push(`เลขที่ ${houseNumber}`);
    if (projectName) addressParts.push(projectName);
    if (soi) addressParts.push(`ซอย ${soi}`);
    if (road) addressParts.push(`ถนน ${road}`);
    if (subdistrict) addressParts.push(`ตำบล${subdistrict}`);
    if (district) addressParts.push(`อำเภอ${district}`);
    if (province) addressParts.push(`จังหวัด${province}`);
    if (postalCode) addressParts.push(postalCode);
    
    return addressParts.length > 0 ? addressParts.join(' ') : '-';
  }

  /**
   * Get professional training form array
   */
  get professionalTrainingArray(): FormArray {
    return this.createUserForm.get('professionalTraining') as FormArray;
  }

  /**
   * Add professional training row
   */
  addProfessionalTraining(): void {
    const trainingGroup = this.fb.group({
      trainingTopic: ['', Validators.required],
      organizer: ['', Validators.required],
      cpdHours: [0, [Validators.min(0), Validators.max(100)]],
      trainingDate: ['', Validators.required]
    });
    
    this.professionalTrainingArray.push(trainingGroup);
  }

  /**
   * Remove professional training row
   */
  removeProfessionalTraining(index: number): void {
    if (this.professionalTrainingArray.length > 1) {
      this.professionalTrainingArray.removeAt(index);
    }
  }

  /**
   * Handle training topic change (auto-set organizer if applicable)
   */
  onTrainingTopicChange(index: number, event: any): void {
    const selectedTopic = event.target.value;
    const trainingGroup = this.professionalTrainingArray.at(index);
    
    // Auto-set organizer based on training topic
    if (selectedTopic.includes('VAT') || selectedTopic.includes('การประเมินค่าอสังหาริมทรัพย์') || 
        selectedTopic.includes('จรรยาบรรณ') || selectedTopic.includes('มาตรฐาน')) {
      trainingGroup.get('organizer')?.setValue('VAT');
    } else if (selectedTopic.includes('หลักทรัพย์') || selectedTopic.includes('การเงิน') || 
               selectedTopic.includes('กฎหมายตลาดทุน') || selectedTopic.includes('กำกับดูแล')) {
      trainingGroup.get('organizer')?.setValue('กลต.');
    }
  }

  /**
   * Get total CPD hours from all training records
   */
  getTotalCPDHours(): number {
    let totalHours = 0;
    this.professionalTrainingArray.controls.forEach(control => {
      const hours = control.get('cpdHours')?.value || 0;
      totalHours += parseFloat(hours);
    });
    return totalHours;
  }

  /**
   * Get other training form array
   */
  get otherTrainingArray(): FormArray {
    return this.createUserForm.get('otherTraining') as FormArray;
  }

  /**
   * Add other training row
   */
  addOtherTraining(): void {
    const trainingGroup = this.fb.group({
      courseName: ['', Validators.required],
      organizer: ['', Validators.required],
      cpdHours: [0, [Validators.min(0), Validators.max(100)]],
      courseDate: ['', Validators.required],
      status: ['']
    });
    
    this.otherTrainingArray.push(trainingGroup);
  }

  /**
   * Remove other training row
   */
  removeOtherTraining(index: number): void {
    if (this.otherTrainingArray.length > 1) {
      this.otherTrainingArray.removeAt(index);
    }
  }

  /**
   * Get total CPD hours from other training records
   */
  getTotalOtherCPDHours(): number {
    let totalHours = 0;
    this.otherTrainingArray.controls.forEach(control => {
      const hours = control.get('cpdHours')?.value || 0;
      totalHours += parseFloat(hours);
    });
    return totalHours;
  }

  /**
   * Close modal
   */
  close(): void {
    this.activeModal.close();
  }

  /**
   * Dismiss modal
   */
  dismiss(): void {
    this.activeModal.dismiss();
  }
} 